import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

interface PermissionResponse {
	permissions: Record<string, any>;
}

@Injectable({ providedIn: 'root' })
export class PermissionService {
	private http = inject(HttpClient);
	private permissions: Record<string, any> = {};
	private _isGlobalUser = false;
	private result: Promise<any> | null = null;

	constructor() {
		this._isGlobalUser = localStorage.getItem('isGlobalUser') === 'true';
		// Don't automatically fetch permissions on service creation
		// Let components fetch them when they actually need them and user is authenticated
	}

	/** Check if user is authenticated by checking localStorage directly (avoiding circular dependency) */
	private isUserAuthenticated(): boolean {
		const token = localStorage.getItem('token');
		const tokenExpiresAt = localStorage.getItem('tokenExpiresAt');
		
		if (!token || !tokenExpiresAt) {
			return false;
		}

		// Check if token is expired
		const expirationTime = parseInt(tokenExpiresAt);
		const now = new Date().getTime();
		
		if (now >= expirationTime) {
			console.log('PermissionService: Token expired');
			return false;
		}
		
		return true;
	}

	/** Fetch permissions from the server */
	public getPermissions(): Promise<any> {
		return firstValueFrom(
			this.http.get<PermissionResponse>('/api/User/CurrentPermissions')
		).then(response => {
			this.permissions = response.permissions;
			this._isGlobalUser = this.permissions['Global.IsGlobalUser'] === true;
			localStorage.setItem('isGlobalUser', String(this._isGlobalUser));
			return response;
		}).catch(error => {
			console.error('Failed to fetch permissions:', error);
			// Reset cached result on error so it can be retried
			this.result = null;
			throw error;
		});
	}

	/** Check if the current user is global */
	public isGlobalUser(): boolean {
		return this._isGlobalUser;
	}

	/** Get current permissions, caching the result */
	public getCurrentPermissions(): Promise<any> | null {
		// Check if user is authenticated before making API call
		if (!this.isUserAuthenticated()) {
			console.log('PermissionService: User not authenticated, skipping permissions fetch');
			return null;
		}

		if (!this.result) {
			this.result = this.getPermissions();
		}
		return this.result;
	}

	/** Clear cached permissions (useful for re-authentication) */
	public clearCache(): void {
		this.result = null;
		this.permissions = {};
		this._isGlobalUser = false;
	}

	/** Public method to check authentication status (useful for components) */
	public isAuthenticated(): boolean {
		return this.isUserAuthenticated();
	}
}
